
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../mocks/blogPosts';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);

  const categories = ['All', 'Supply Chain', 'Beauty Products', 'Battery Technology', 'Industry Insights', 'Company News'];

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Blog - XJ Global | Industry Insights & Supply Chain News';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Stay updated with XJ Global\'s latest insights on supply chain management, beauty products, battery technology, and industry trends.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "XJ Global Blog",
      "description": "Industry insights and news from XJ Global on supply chain management, beauty products, and battery technology",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "XJ Global",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
        }
      },
      "blogPost": blogPosts.slice(0, 6).map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog/${post.slug}`,
        "datePublished": post.publishDate,
        "dateModified": post.publishDate,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "image": post.image
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      // Reset title
      document.title = 'XJ Global - Leading Global Supply Chain Solutions';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'XJ Global is a leading sourcing, product development and supply chain management company for the world\'s largest retail chain stores and brands.');
      }
    };
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredPosts(blogPosts);
    } else {
      setFilteredPosts(blogPosts.filter(post => post.category === selectedCategory));
    }
  }, [selectedCategory]);

  const featuredPost = blogPosts[0];
  const regularPosts = filteredPosts.slice(selectedCategory === 'All' ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              XJ Global Blog
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Stay updated with the latest insights on supply chain management, product development, and industry trends from our expert team
            </p>
          </div>
        </div>
      </div>

      {/* Featured Post */}
      {selectedCategory === 'All' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-64 md:h-full object-cover object-top"
                />
              </div>
              <div className="md:w-1/2 p-8">
                <div className="flex items-center mb-4">
                  <span className="bg-teal-100 text-teal-800 text-sm font-medium px-3 py-1 rounded-full">
                    Featured
                  </span>
                  <span className="ml-3 text-sm text-gray-500">{featuredPost.category}</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-600 mb-6 text-lg">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img 
                      src={`https://public.readdy.ai/ai/img_res/433a6a8e6a943fc5d872f96127c3a9e5.jpg$%7BfeaturedPost.author%7D%20headshot&width=40&height=40&seq=author-${featuredPost.id}&orientation=squarish`}
                      alt={featuredPost.author}
                      className="w-10 h-10 rounded-full object-cover object-top mr-3"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{featuredPost.author}</p>
                      <p className="text-sm text-gray-500">{featuredPost.publishDate}</p>
                    </div>
                  </div>
                  <Link 
                    to={`/blog/${featuredPost.slug}`}
                    className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Read More
                    <div className="ml-2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap ${
                selectedCategory === category
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-teal-50 hover:text-teal-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regularPosts.map((post) => (
            <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                  <span className="text-sm text-gray-500">{post.readTime}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img 
                      src={`https://readdy.ai/api/search-image?query=professional%20business%20person%20portrait%20$%7Bpost.author%7D%20headshot&width=32&height=32&seq=author-${post.id}-small&orientation=squarish`}
                      alt={post.author}
                      className="w-8 h-8 rounded-full object-cover object-top mr-2"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{post.author}</p>
                      <p className="text-xs text-gray-500">{post.publishDate}</p>
                    </div>
                  </div>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                  >
                    Read More →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {regularPosts.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
            <p className="text-gray-500">Try selecting a different category.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
